import React from 'react';
import { Home, BarChart3, Bitcoin, TrendingUp, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { formatDistanceToNow } from 'date-fns';

interface DataSourceTooltipProps {
  sources?: {
    rates: 'api_ninjas' | 'mock' | 'cache';
    stocks: 'fmp' | 'mock' | 'cache';
    crypto: 'coingecko' | 'mock' | 'cache';
    trends: 'redfin' | 'mock' | 'cache';
  };
  timestamps?: {
    rates?: string;
    stocks?: string;
    crypto?: string;
    trends?: string;
  };
}

interface DataSourceInfo {
  category: string;
  source: string;
  isReal: boolean;
  lastUpdated: string;
  icon: React.ComponentType<{ className?: string }>;
}

const formatSource = (source?: string): string => {
  const sourceMap: Record<string, string> = {
    'api_ninjas': 'API Ninjas',
    'fmp': 'FMP',
    'coingecko': 'CoinGecko',
    'redfin': 'Redfin',
    'cache': 'Cached (Real)',
    'mock': 'Mock Data'
  };
  return sourceMap[source || 'mock'] || 'Unknown';
};

export const DataSourceTooltip: React.FC<DataSourceTooltipProps> = ({ sources, timestamps }) => {
  const sourceInfo: DataSourceInfo[] = [
    {
      category: 'Interest Rates',
      source: formatSource(sources?.rates),
      isReal: sources?.rates !== 'mock',
      lastUpdated: timestamps?.rates || 'Unknown',
      icon: Home
    },
    {
      category: 'Stock Indices',
      source: formatSource(sources?.stocks),
      isReal: sources?.stocks !== 'mock',
      lastUpdated: timestamps?.stocks || 'Unknown',
      icon: BarChart3
    },
    {
      category: 'Cryptocurrency',
      source: formatSource(sources?.crypto),
      isReal: sources?.crypto !== 'mock',
      lastUpdated: timestamps?.crypto || 'Unknown',
      icon: Bitcoin
    },
    {
      category: 'Market Trends',
      source: formatSource(sources?.trends),
      isReal: sources?.trends !== 'mock',
      lastUpdated: timestamps?.trends || 'Unknown',
      icon: TrendingUp
    }
  ];

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button className="inline-flex items-center justify-center">
            <Info className="w-4 h-4 text-muted-foreground hover:text-foreground transition-colors cursor-help" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="w-80 p-4" side="bottom">
          <div className="space-y-3">
            <p className="font-semibold text-sm text-foreground">Data Source Breakdown</p>
            <div className="space-y-2">
              {sourceInfo.map((info) => {
                const Icon = info.icon;
                const updateTime = info.lastUpdated !== 'Unknown' 
                  ? formatDistanceToNow(new Date(info.lastUpdated), { addSuffix: true })
                  : 'Unknown';
                
                return (
                  <div key={info.category} className="flex items-start justify-between gap-3 p-2 rounded-md bg-background/50">
                    <div className="flex items-center gap-2 flex-1">
                      <Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-foreground">{info.category}</div>
                        <div className="text-[10px] text-muted-foreground truncate">{updateTime}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {info.isReal ? (
                        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                      ) : (
                        <AlertCircle className="w-3.5 h-3.5 text-yellow-500" />
                      )}
                      <span className="text-[10px] font-medium text-muted-foreground">{info.source}</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="pt-2 border-t border-border/50">
              <p className="text-[10px] text-muted-foreground leading-relaxed">
                <span className="font-medium text-foreground">Real sources:</span> API Ninjas (Rates), FMP (Stocks), CoinGecko (Crypto), Redfin (Trends)
              </p>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
